import React, { useState, useEffect } from "react";
import { useTheme } from "../contexts/ThemeContext";

/**
 * A single dollar amount with a Save button — the monthly budget and the
 * monthly income goal.
 *
 * Owns the draft string so a half-typed value never reaches app state, and
 * re-syncs from `value` when the profile finishes loading or a save is rolled
 * back.
 *
 * @param {Object} props
 * @param {string} props.title
 * @param {number|null} props.value
 * @param {(parsed: number) => void} props.onSave
 * @param {string} [props.hint]
 */
const MonthlyAmountCard = ({ title, value, onSave, hint }) => {
  const { theme } = useTheme();
  const gradient = theme.colors.gradient;
  const [draft, setDraft] = useState(String(value ?? ""));
  const [error, setError] = useState("");

  useEffect(() => {
    setDraft(String(value ?? ""));
  }, [value]);

  const handleSave = () => {
    const parsed = Number(draft);

    if (draft === "" || !Number.isFinite(parsed) || parsed < 0) {
      setError("Enter an amount of 0 or more.");
      return;
    }

    setError("");
    onSave(parsed);
  };

  return (
    <div className="bg-white rounded-2xl shadow-soft border border-gray-100 p-4 mb-4">
      <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
        {title}
      </h4>
      <div className="flex gap-2">
        <div className="flex-1 relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">$</span>
          <input
            type="number"
            inputMode="decimal"
            min="0"
            step="1"
            value={draft}
            onChange={(e) => {
              setDraft(e.target.value);
              setError("");
            }}
            aria-label={title}
            aria-invalid={error !== ""}
            className="w-full p-3 pl-7 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-transparent transition-all"
          />
        </div>
        <button
          onClick={handleSave}
          className={`p-3 px-5 rounded-xl text-white font-medium bg-gradient-to-r ${gradient.button} ${gradient.buttonHover} shadow-md ${theme.colors.shadow} transition-all active:scale-95`}
        >
          Save
        </button>
      </div>
      {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
      {!error && hint && <p className="mt-2 text-xs text-gray-400">{hint}</p>}
    </div>
  );
};

export default MonthlyAmountCard;
